type AtomOAuthScope = 'READ_PROFILE' | 'READ_BOOKING';

export interface ICalcomOAuthDetails {
  oAuthClientId: string;
  redirectUri: string;
  scopes: AtomOAuthScope[];
  state: string;
}

/* One row per calendar the user has connected on Cal.com. `provider` is the integration slug
 * (e.g. 'google', 'office365'); `title` is its display name; `email` is the primary calendar's. */
export interface ICalcomConnectedCalendar {
  provider: string;
  title: string;
  email: string | null;
}

export interface ICalcomConnectionStatus {
  connected: boolean;
  calcomUserId: number | null;
  calcomUsername: string | null;
  scope: string | null;
  connectedCalendars: ICalcomConnectedCalendar[];
}

export interface ICalcomOAuthExchangeRequest {
  code: string;
  state: string;
}

export interface ICalcomStartAuthorization {
  authorizeUrl: string;
}

/* The user who already owns the Cal.com integration a connect attempt collided with. Fetched by
 * conflictIntegrationId after the OAuth bounce so we can tell the current user who has the seat. */
export interface ICalcomConflictingIntegration {
  email: string;
  firstName: string;
  lastName: string;
  calcomUsername: string;
}

export interface ICalcomApiResult<T> {
  ok: boolean;
  data?: T;
  error?: string;
  status?: number;
}

// The three booking-link kinds, matching the backend JuniperEventType enum.
export enum JuniperEventType {
  Walkthrough = 1,
  ServiceRequest = 2,
  KeyPickup = 3,
}

// A property the current user is allowed to manage booking links for (existing Juniper entity).
export interface IUserAssociatedProperty {
  propertyId: string;
  name: string;
}

/* A Cal.com event type on the connected user's account — the availability source a booking link
 * points at. Juniper can only read these; users create them in Cal.com. */
export interface ICalcomEventType {
  id: number;
  slug: string;
  title: string;
  lengthInMinutes: number;
  hidden: boolean;
  bookingUrl: string;
  ownerId: number;
}

// A Juniper-side pointer: property + kind + label → a Cal.com event type owned by a connected user.
export interface IPropertyBookingLink {
  id: string;
  propertyId: string;
  calcomIntegrationId: string;
  kind: JuniperEventType;
  label: string;
  calcomEventTypeId: number;
  url: string;
  ownerFirstName: string | null;
  ownerLastName: string | null;
}

export interface ICreatePropertyBookingLinkRequest {
  propertyId: string;
  label: string;
  kind: JuniperEventType;
  calcomEventTypeId: number;
}

// Edit an existing link. Label is replaced; isActive and event type are optional partial updates.
export interface IUpdatePropertyBookingLinkRequest {
  label: string;
  isActive?: boolean;
  calcomEventTypeId?: number;
}

/* Lifecycle state for a unit's key pickup, derived server-side from the latest non-deleted
 * ScheduleEvent (+ the sticky IsKeyHandOff flag, which overrides all and is set only by the
 * mobile signature flow). Values mirror the backend KeyPickupStatuses constants exactly. */
export type KeyPickupStatus = 'Pending' | 'InviteSent' | 'Booked' | 'HandedOff';

/* A unit's key-pickup status for a property — drives the per-property invite table.
 * homeOwnerFullName is nullable: a unit may have no homeowner on file yet.
 * calcomBookingUid/startTime are only populated when keyPickupStatus is 'Booked'. */
export interface IUnitKeyPickupStatus {
  unitId: string;
  number: string;
  homeOwnerFullName: string | null;
  isKeyHandOff: boolean;
  keyPickupStatus: KeyPickupStatus;
  calcomBookingStatus: string | null;
  calcomBookingUid: string | null;
  startTime: string | null;
}

/* Per-property counts for the key-pickups card grid (one bulk fetch, no N+1).
 * invitableCount = Pending AND has a homeowner with an email (actually sendable); the status
 * counts roll up the latest-event lifecycle so the card can show progress. */
export interface IKeyPickupSummary {
  propertyId: string;
  totalCount: number;
  invitableCount: number;
  inviteSentCount: number;
  bookedCount: number;
  handedOffCount: number;
}

export interface IBulkInviteKeyPickupsRequest {
  unitIds: string[];
  propertyBookingLinkId: string;
}